import React from "react";

/**
 * Public portfolio reel page — one URL for job applications (Tether, etc.)
 * Tatva K · AI Film Selects · 2024–25
 */

const BG = "#0a0a0c";
const CARD = "#13131a";
const INK = "#f4ede0";
const BRONZE = "#c9a227";
const MUTED = "#9a8c70";
const SERIF = '"Playfair Display", Georgia, "Times New Roman", serif';
const SANS = "Inter, -apple-system, BlinkMacSystemFont, sans-serif";

const VIDEOS = [
    {
        title: "Tethered",
        tag: "Built for Tether AI Filmmaker application",
        meta: "60 seconds · Kaiber AI · MidJourney · Final Cut Pro X · Original score",
        src: "https://customer-assets.emergentagent.com/job_nervous-system-reset-6/artifacts/f0q9nyx1_Tether%20Dummy.mp4",
        poster: "/static/reel-thumbs/tether.jpg",
        hero: true,
    },
    {
        title: "Afrochella — Cover Art Drop",
        tag: "Music industry concept piece",
        meta: "Kaiber AI · Beat-synced motion · Music-industry visual",
        src: "https://customer-assets.emergentagent.com/job_nervous-system-reset-6/artifacts/nofy98y6_Afrochella%20cover%20art%20explodes%20out%20of%20a%20beam%20of%20golden%20light%20in%20the%20night%20sky%2C%20surrounded%20by%20vinyl%20shards%2C%20Spotify%20logos%2C%20and%20neon%20tribal%20runes.%20Album%20pulses%20in%20sync%20with%20beat..mp4",
        poster: "/static/reel-thumbs/afrochella.jpg",
    },
    {
        title: "Futuristic Jungle Rave",
        tag: "Afro-futurist cinematic",
        meta: "Kaiber AI · Camera swirl shot · Hologram composition",
        src: "https://customer-assets.emergentagent.com/job_nervous-system-reset-6/artifacts/zzmpoxa0_Futuristic%20jungle%20rave_%20dancers%20in%20body%20paint%20moving%20around%20a%20glowing%20bonfire%2C%20holograms%20of%20iktara%20strings%20circling%20in%20the%20air%2C%20Afro-futuristic%20DJ%20console%20glowing%20in%20the%20background.%20Camera%20swirl%20shot..mp4",
        poster: "/static/reel-thumbs/jungle.jpg",
    },
    {
        title: "Halo Pulse",
        tag: "Wellness brand motion",
        meta: "Kaiber AI · Subtle motion · Brand visual for The Reset Sanctuary",
        src: "https://customer-assets.emergentagent.com/job_nervous-system-reset-6/artifacts/7az2ly0e_can%20you%20pls%20anumate%20this%20picture%20with%20the%20halo%20circulating%20and%20pulse%20coming%20out%20in%20all%20dorections%20subtely.mp4",
        poster: "/static/reel-thumbs/halo.jpg",
    },
    {
        title: "What is Ethereum? — Hindi Explainer",
        tag: "Long-form crypto education (~100K views)",
        meta: "Written, voiced, animated and edited in-house · Original infographics · Hindi narration",
        embed: "https://www.youtube.com/embed/KpeQF-0nfQc",
    },
];

const VideoCard = ({ title, tag, meta, src, embed, poster, hero }) => (
    <article
        data-testid={`reel-card-${title.toLowerCase().replace(/\s+/g, "-")}`}
        style={{
            backgroundColor: CARD,
            borderRadius: 14,
            overflow: "hidden",
            border: `1px solid rgba(201,162,39,${hero ? 0.4 : 0.15})`,
            boxShadow: hero ? "0 24px 60px rgba(201,162,39,0.18)" : "0 12px 28px rgba(0,0,0,0.35)",
            marginBottom: 28,
        }}
    >
        {embed ? (
            <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, backgroundColor: "#000" }}>
                <iframe
                    src={embed}
                    title={title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
                />
            </div>
        ) : (
            <video
                src={src}
                poster={poster}
                controls
                playsInline
                preload="metadata"
                style={{
                    width: "100%",
                    display: "block",
                    backgroundColor: "#000",
                    aspectRatio: "16 / 9",
                    objectFit: "cover",
                }}
            />
        )}
        <div style={{ padding: "20px 24px 22px" }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <h3 style={{ fontFamily: SERIF, color: INK, fontSize: hero ? 26 : 20, margin: 0, fontWeight: 500, letterSpacing: "-0.01em" }}>
                    {title}
                </h3>
                {hero && (
                    <span style={{ fontFamily: SANS, color: BRONZE, fontSize: 9, letterSpacing: "0.3em", textTransform: "uppercase", fontWeight: 700, border: `1px solid ${BRONZE}`, padding: "4px 10px", borderRadius: 999 }}>
                        Featured
                    </span>
                )}
            </div>
            <p style={{ fontFamily: SERIF, fontStyle: "italic", color: BRONZE, fontSize: 13, margin: "6px 0 0" }}>{tag}</p>
            <p style={{ fontFamily: SANS, color: MUTED, fontSize: 11, margin: "10px 0 0", letterSpacing: "0.04em", lineHeight: 1.55 }}>{meta}</p>
        </div>
    </article>
);

const Reel = () => (
    <main
        data-testid="reel-page"
        style={{
            backgroundColor: BG,
            minHeight: "100vh",
            color: INK,
            fontFamily: SANS,
            padding: "60px 20px 80px",
        }}
    >
        <div style={{ maxWidth: 880, margin: "0 auto" }}>
            <header style={{ marginBottom: 40, paddingBottom: 24, borderBottom: "1px solid rgba(201,162,39,0.2)" }}>
                <p style={{ fontFamily: SANS, color: BRONZE, fontSize: 10, letterSpacing: "0.36em", textTransform: "uppercase", fontWeight: 600, margin: 0 }}>
                    AI Film Selects · 2024–25
                </p>
                <h1 style={{ fontFamily: SERIF, fontSize: 48, fontWeight: 500, color: INK, margin: "10px 0 0", letterSpacing: "-0.02em", lineHeight: 1 }}>
                    Aditya Anand
                </h1>
                <p style={{ fontFamily: SERIF, fontStyle: "italic", color: BRONZE, fontSize: 16, margin: "8px 0 0" }}>
                    Known professionally as Tatva K · AI Filmmaker · Sound Engineer · Crypto &amp; AI Operator
                </p>
                <p style={{ fontFamily: SANS, color: MUTED, fontSize: 12, margin: "16px 0 0", letterSpacing: "0.04em", lineHeight: 1.7 }}>
                    Al Hamra Village, Ras Al Khaimah, UAE &nbsp;·&nbsp; tatvak@theresetsanctuary.org &nbsp;·&nbsp; +971 50 676 9007
                    <br />
                    Pipeline: Kaiber AI (primary) · Runway · Pika · MidJourney · Final Cut Pro X · Ableton Live (in-house score)
                </p>
            </header>

            {VIDEOS.map((v) => (
                <VideoCard key={v.title} {...v} />
            ))}

            <footer style={{ marginTop: 40, paddingTop: 28, borderTop: "1px solid rgba(201,162,39,0.2)", textAlign: "center" }}>
                <p style={{ fontFamily: SERIF, fontStyle: "italic", color: MUTED, fontSize: 13, margin: 0 }}>
                    Full body of work and references available on request.
                </p>
                <p style={{ fontFamily: SANS, color: BRONZE, fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", fontWeight: 600, margin: "12px 0 0" }}>
                    Twenty years across live, broadcast and studio · 200M+ streams · TEDx · Berklee
                </p>
            </footer>
        </div>
    </main>
);

export default Reel;
